import { Box, Button, FormControl } from "@material-ui/core";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import Checkbox from "@saleor/components/Checkbox";
import MyTypography from "@saleor/components/MyTypography";
import SingleTextField from "@saleor/components/SingleTextField";
import {
  GetBusinessUnitQuery,
  useGradeGramSearchSuggestionLazyQuery,
  useMaterialSearchSuggestionLazyQuery
} from "@saleor/graphql";
import useBulkActions from "@saleor/hooks/useBulkActions";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { RelayToFlat } from "@saleor/types";
import { mapBuToCompany, mapEdgesToItems } from "@saleor/utils/maps";
import classNames from "classnames";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const RetryIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M16.919 13H12.5H16.919Z" fill="#222222" />
    <path
      d="M1.5 1.99999V6.99999H2.082M2.082 6.99999C2.74585 5.35812 3.93568 3.9829 5.46503 3.08985C6.99438 2.1968 8.7768 1.8364 10.533 2.06513C12.2891 2.29386 13.9198 3.09878 15.1694 4.35377C16.419 5.60875 17.2168 7.24285 17.438 8.99999M2.082 6.99999H6.5M17.5 18V13H16.919M16.919 13C16.2542 14.6409 15.064 16.015 13.5348 16.9073C12.0056 17.7995 10.2237 18.1595 8.4681 17.9309C6.71246 17.7022 5.0822 16.8979 3.83253 15.6437C2.58287 14.3896 1.78435 12.7564 1.562 11M16.919 13H12.5"
      // stroke={color || "#009FB8"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const useStyle = makeStyles(
  theme => ({
    root: {
      display: "flex",
      alignItems: "center"
    },
    wrapper: {
      width: "100%",
      marginTop: "",
      padding: "1.6rem",
      background: "white",
      border: `1px solid ${theme.palette.info.main}`,
      borderRadius: "6px"
    },
    container: {
      padding: "15px 20px",
      border: `1px solid ${theme.palette.info.main}`,
      marginBottom: "10px"
    },
    disabled: {
      padding: "15px 20px",
      border: `1px solid ${theme.palette.grey["300"]}`,
      marginBottom: "10px"
    },
    content: {
      borderBottom: `1px solid ${theme.palette.grey["300"]}`
    },
    contentItem: {
      paddingLeft: "4.8rem",
      color: theme.palette.grey[900]
    },
    btnContainer: {
      display: "flex",
      justifyContent: "space-between",
      "& .MuiButton-root": {
        width: "48%"
      }
    },
    input: {
      // margin: "24px 0",
      width: "100%"
    },
    title: {
      marginBottom: "1.8rem",
      marginTop: "3rem"
    },
    searchField: {
      marginTop: 18
    }
  }),
  { name: "SoldToFilter" }
);

interface ISoldToFilterProps {
  disabledPrice?: boolean;
  searchId: string;
  businessUnits: RelayToFlat<GetBusinessUnitQuery["businessUnits"]>;
  onFilterByCompanyIds: (ids: string[]) => void;
  onFilterByContractId: (
    id: string | null,
    gradeGram: string | null,
    materialCode: string | null
  ) => void;
}
function indeterminate(ids: string[], listIds: string[]) {
  if (
    ids.some(id => listIds.indexOf(id) !== -1) &&
    !ids.every(id => listIds.indexOf(id) !== -1)
  ) {
    return true;
  }

  return false;
}
const SoldToFilter: React.FC<ISoldToFilterProps> = ({
  businessUnits,
  onFilterByCompanyIds,
  onFilterByContractId
}) => {
  const classes = useStyle();
  const [contractId, setContractId] = useState("");
  const [gradeGramId, setGradeGramId] = useState("");
  const [materialCode, setMaterialCode] = useState("");
  const [isReset, setIsReset] = useState(false);
  const intl = useIntl();

  const {
    toggle: toggleBu,
    isSelected: isSelectedBu,
    remove: removeBu,
    add: addBu
  } = useBulkActions(businessUnits.map(item => item.id));

  const {
    isSelected: isSelectedCompany,
    toggle: toggleCompany,
    listElements: listCompany,
    set: setListCompany
  } = useBulkActions(mapBuToCompany(businessUnits));

  const handleFilterContract = (event: ChangeEvent<HTMLInputElement>) => {
    const currentValue = event.target.value;
    if (!currentValue || new RegExp("^[0-9]*$").test(currentValue)) {
      setContractId(event.target.value);
    }
  };

  const handleResetContract = () => {
    setContractId("");
    setIsReset(false);
    setTimeout(() => {
      setIsReset(true);
    }, 100);
    onFilterByContractId(null, null, null);
  };

  const handleBusinessChange = (checked: boolean, id: string) => {
    toggleBu(id);
    const currentBu = businessUnits.find(bu => bu.id === id);
    const otherBuCompanyIds = businessUnits
      .filter(bu => bu.id !== id)
      .map(bu => bu.companies.map(company => company.id))
      .flat();
    const leftListCompany = [
      ...listCompany.filter(companyId => otherBuCompanyIds.includes(companyId))
    ];
    const rightListCompany = currentBu.companies.map(company => company.id);
    if (checked) {
      setListCompany([...leftListCompany, ...rightListCompany]);
    } else {
      setListCompany(leftListCompany);
    }
  };

  const handleCompanyChange = (checked: boolean, id: string) => {
    toggleCompany(id);
    const currentBu = businessUnits.find(bu =>
      bu.companies.map(item => item.id).includes(id)
    );
    const currentBuCompanyIds = currentBu.companies.map(company => company.id);
    if (checked) {
      const newBuListCompany = [...listCompany]
        .filter(companyId => currentBuCompanyIds.includes(companyId))
        .concat([id]);
      if (newBuListCompany.length === currentBuCompanyIds.length) {
        addBu(currentBu.id);
      }
    } else {
      removeBu(currentBu.id);
    }
  };

  const [
    getGradeGramDataSuggestions,
    { data: gradeGramDataSuggestions, loading: loadingGradeGramDataSuggestion }
  ] = useGradeGramSearchSuggestionLazyQuery();

  const dataOptions = (
    mapEdgesToItems(gradeGramDataSuggestions?.materialSearchSuggestion) || []
  ).map(data => ({
    title: data.code,
    value: data.code
  }));

  const [
    getMaterialDataSuggestions,
    { data: materialDataSuggestions, loading: loadingMaterialDataSuggestion }
  ] = useMaterialSearchSuggestionLazyQuery();

  const materialDataOptions = (
    mapEdgesToItems(materialDataSuggestions?.materialSearchSuggestion) || []
  ).map(data => ({
    title: `${data.code}`,
    value: `${data.code}`
  }));

  useEffect(() => {
    onFilterByCompanyIds(listCompany);
  }, [listCompany]);

  const idDisablegradeGram = useMemo(() => !!materialCode, [materialCode]);
  const idDisableMaterialCode = useMemo(() => !!gradeGramId, [gradeGramId]);

  return (
    <>
      <form>
        <MyTypography variant="h7" className={classes.title}>
          <FormattedMessage defaultMessage="ตัวเลือกการค้นหา" />
        </MyTypography>
        <FormControl className={classes.wrapper}>
          <>
            <MyTypography variant="subtitle3">
              {intl.formatMessage({
                defaultMessage: "เลือกจากโครงการ (Contract)"
              })}
            </MyTypography>
            <div>
              {renderCollection(businessUnits, item => {
                const ids = item.companies.map(item => item.id);
                const indeterminateBool = indeterminate(ids, listCompany);
                return (
                  <div className={classes.content} key={item.id}>
                    <div className={classes.root}>
                      <Checkbox
                        indeterminate={indeterminateBool}
                        checked={isSelectedBu(item.id)}
                        onChange={e =>
                          handleBusinessChange(e.currentTarget.checked, item.id)
                        }
                        id="checkboxBu"
                        name="checkboxBu"
                      />
                      <MyTypography variant="subtitle3">
                        {item.name}
                      </MyTypography>
                    </div>
                    <div className={classes.contentItem}>
                      <MyTypography variant="body3">Company</MyTypography>
                      {renderCollection(item.companies, company => (
                        <div key={company.id}>
                          <Checkbox
                            checked={isSelectedCompany(company.id)}
                            onChange={e =>
                              handleCompanyChange(
                                e.currentTarget.checked,
                                company.id
                              )
                            }
                            id={`checkboxCompany-${company.id}`}
                          />
                          <span>{company?.shortName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <>
              <Box className={classes.searchField}>
                <Box mb={1}>
                  <MyTypography variant="captionBold">
                    <FormattedMessage defaultMessage="ค้นหาเลขที่ Contract" />
                  </MyTypography>
                </Box>
                <SingleTextField
                  // labelFocus="ระบุเลขที่ Contract"
                  className={classes.input}
                  onChange={handleFilterContract}
                  placeholder="ระบุเลขที่ Contract"
                  value={contractId}
                  id="contract"
                  name="contract"
                />
              </Box>
              <Box className={classes.searchField}>
                <Box mb={1}>
                  <MyTypography variant="captionBold">
                    <FormattedMessage defaultMessage="ค้นหา Grade/Gram" />
                  </MyTypography>
                </Box>
                <InputSuggestion
                  searchField="gradeGram"
                  dataOptions={dataOptions}
                  loadingDataSuggestion={loadingGradeGramDataSuggestion}
                  objectSuggestion
                  getDataSuggestions={getGradeGramDataSuggestions}
                  contained
                  totalCount={dataOptions.length}
                  isCheckingRole
                  disabled={idDisablegradeGram}
                  name={"gradeGram"}
                  id="gradeGram"
                  placeholder={"ระบุ Grade/Gram"}
                  // error={!!error}
                  isClean={isReset}
                  // onMouseLeave={() => setForcus(false)}
                  handleSelectedSearch={selected => {
                    setGradeGramId(selected.value);
                  }}
                />
              </Box>
              <Box className={classes.searchField}>
                <Box mb={1}>
                  <MyTypography variant="captionBold">
                    <FormattedMessage defaultMessage="ค้นหา Mat. Code" />
                  </MyTypography>
                </Box>
                {/* <SingleTextField
                value=""
                onChange={() => {}}
                placeholder="ระบุ Mat. Code"
              /> */}
                <InputSuggestion
                  searchField="materialCode"
                  dataOptions={materialDataOptions}
                  loadingDataSuggestion={loadingMaterialDataSuggestion}
                  getDataSuggestions={getMaterialDataSuggestions}
                  contained
                  totalCount={materialDataOptions.length}
                  isCheckingRole
                  disabled={idDisableMaterialCode}
                  name={"materialOs"}
                  id="materialCode"
                  placeholder={"ระบุ Mat. Code"}
                  objectSuggestion
                  isClean={isReset}
                  // error={!!error}
                  handleSelectedSearch={selected => {
                    setMaterialCode(selected.value);
                  }}
                />
              </Box>
              <Box
                className={classNames(
                  classes.btnContainer,
                  classes.searchField
                )}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={!(contractId || gradeGramId || materialCode)}
                  onClick={handleResetContract}
                  id="resetContractButton"
                  name="resetContractButton"
                >
                  <RetryIcon />
                  ล้างข้อมูล
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!contractId && !gradeGramId && !materialCode}
                  type="button"
                  onClick={() =>
                    onFilterByContractId(contractId, gradeGramId, materialCode)
                  }
                  id="searchContractButton"
                  name="searchContractButton"
                >
                  ค้นหา
                </Button>
              </Box>
            </>
          </>
        </FormControl>
      </form>
    </>
  );
};
SoldToFilter.displayName = "SoldToFilter";
export default SoldToFilter;
